/**
 * VoidPaymentsModal — audit list of payments currently marked void /
 * refunded / cancelled / bounced. These are the payments Collections
 * aggregators skip, so managers can eyeball exactly what was filtered out.
 *
 * Wired from BooksAnalyticsPage overview header (Ban icon button).
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Ban, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtInr = (n) => (n == null || isNaN(n)) ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`;

export default function VoidPaymentsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get('/books/void-payments')
      .then(r => setData(r.data))
      .catch(() => setData({ count: 0, payments: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    load();
  }, [open]);

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/books/payments/backfill-status');
      toast.success(r.data.message || 'Statuses refreshed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="void-payments-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-rose-600" /> Void Payments · excluded from Collections
            </span>
            <button type="button" onClick={refreshStatuses} disabled={refreshing || loading}
              data-testid="void-refresh-statuses"
              className="text-[10px] px-2 py-1 rounded-md border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-60 flex items-center gap-1">
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {refreshing ? 'Refreshing…' : 'Refresh from Zoho'}
            </button>
          </DialogTitle>
          <p className="text-[11px] text-slate-500 mt-1">
            Payments whose status is voided / refunded / cancelled / bounced / failed / returned in Zoho. These <b>are not</b> counted toward any Collections total — this list shows exactly what was filtered out.
            {data && (
              <span className="ml-1">Currently: <b>{data.count} payment{data.count === 1 ? '' : 's'}</b> totalling <b>{fmtInr(data.total_amount)}</b>.</span>
            )}
          </p>
        </DialogHeader>
        <div className="flex-1 overflow-auto border border-slate-200 rounded" data-testid="void-payments-body">
          {loading ? (
            <p className="text-center py-8 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Loading…</p>
          ) : !data || data.count === 0 ? (
            <p className="text-center py-8 text-xs text-slate-400 italic">No void payments detected in the last 1,000 payment records. Trigger <b>Sync from Zoho</b> after voiding a payment to see it here.</p>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                  <th className="text-left px-3 py-1.5">Payment #</th>
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-left px-3 py-1.5">Customer</th>
                  <th className="text-left px-3 py-1.5">Salesperson</th>
                  <th className="text-left px-3 py-1.5">Mode</th>
                  <th className="text-right px-3 py-1.5">Amount</th>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="text-left px-3 py-1.5">Applied to</th>
                </tr>
              </thead>
              <tbody>
                {(data.payments || []).map((pm) => (
                  <tr key={pm.payment_id} className="border-t border-slate-100 hover:bg-rose-50/30" data-testid={`void-payment-row-${pm.payment_number}`}>
                    <td className="px-3 py-1.5 font-medium text-violet-700 tabular-nums">{pm.payment_number || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-500 tabular-nums">{pm.date}</td>
                    <td className="px-3 py-1.5 text-slate-700 truncate max-w-[200px]" title={pm.customer_name}>{pm.customer_name || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-500 truncate max-w-[120px]" title={pm.salesperson_name}>{pm.salesperson_name || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-500">{pm.payment_mode || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-rose-700">{fmtInr(pm.amount)}</td>
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700 text-[9px] uppercase tracking-wide font-semibold">
                        <Ban className="w-2.5 h-2.5" /> {pm.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-slate-500">
                      {(pm.invoices || []).length === 0 ? (
                        <span className="italic text-slate-400">Unapplied</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {pm.invoices.map((ap, i) => (
                            <span key={i} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-slate-200 bg-white text-slate-600 tabular-nums">
                              {ap.invoice_number || ap.invoice_id?.slice(-6) || '—'}
                              <span className="text-slate-400 ml-0.5">· {fmtInr(ap.amount_applied)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-[10px] text-slate-400 italic mt-1 flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5" />
          Statuses are auto-refreshed after every <b>Sync from Zoho</b> and on the nightly job — click <b>Refresh from Zoho</b> above to pull them right now.
        </p>
      </DialogContent>
    </Dialog>
  );
}
